import React, { useState, useEffect } from 'react';
    import { Link } from 'react-router-dom';
    import { Input } from '@/components/ui/Input';
    import { Button } from '@/components/ui/Button';
    import { useAuth } from '@/contexts/AuthContext';
    import { FiAlertCircle, FiCheckCircle, FiInfo, FiMail, FiTerminal, FiExternalLink } from 'react-icons/fi';
    import toast from 'react-hot-toast';

    export function ForgotPasswordPage() {
      const [email, setEmail] = useState('');
      const [error, setError] = useState('');
      const [success, setSuccess] = useState(false);
      const [isLoading, setIsLoading] = useState(false);
      const [showTroubleshooter, setShowTroubleshooter] = useState(false);
      const [countdown, setCountdown] = useState(0);
      
      const { resetPassword } = useAuth();
      const redirectUrl = `${window.location.origin}/reset-password`;

      useEffect(() => {
        if (countdown > 0) {
          const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
          return () => clearTimeout(timer);
        }
      }, [countdown]);

      const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (countdown > 0) {
          setError(`Please wait ${countdown}s before requesting another link. Supabase rate-limits emails.`);
          return;
        }

        setIsLoading(true);
        console.log('[DEBUG] Requesting reset for:', email);
        console.log('[DEBUG] Redirect URL being sent:', redirectUrl);

        try {
          const { error: resetError } = await resetPassword(email);
          
          if (resetError) {
            console.error('[DEBUG] Supabase Error:', resetError);
            setError(resetError.message);
          } else {
            console.log('[DEBUG] Reset request accepted (200 OK)');
            setSuccess(true);
            setCountdown(60); // Start 1-minute cooldown
            toast.success('Reset email requested!');
          }
        } catch (err) {
          console.error('[DEBUG] Exception:', err);
          setError('An unexpected error occurred.');
        } finally {
          setIsLoading(false);
        }
      };

      if (success) {
        return (
          <div className="space-y-6 text-left">
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiMail className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Email Sent!</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-2 text-sm">
                We've sent a recovery link to: <br/>
                <strong className="text-slate-900 dark:text-white">{email}</strong>
              </p>
            </div>

            <div className="p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl space-y-3">
              <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                <FiInfo className="text-primary-500" />
                If you don't see it:
              </h3>
              <ul className="text-xs text-slate-500 dark:text-slate-400 space-y-2 list-disc pl-4">
                <li>Check your <strong>Spam / Junk</strong> folder.</li>
                <li>Wait up to 5 minutes (Supabase default mail is sometimes slow).</li>
                <li>Ensure you used the correct email address.</li>
              </ul>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={() => setSuccess(false)} 
                variant="outline" 
                className="w-full h-12 rounded-xl font-bold"
                disabled={countdown > 0}
              >
                {countdown > 0 ? `Resend available in ${countdown}s` : 'Try again'}
              </Button>
              <Link to="/login" className="block text-center text-xs font-black uppercase tracking-widest text-primary-500 underline">
                Return to Login
              </Link>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-6 text-left">
          <div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Reset Password</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Enter your email and we'll send you a recovery link.</p>
          </div>

          {error && (
            <div className="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-2xl flex items-start gap-3 text-rose-600 dark:text-rose-400 text-sm">
              <FiAlertCircle className="shrink-0 mt-0.5" />
              <p className="font-bold">{error}</p>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <Input 
              label="Email address" 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              placeholder="your@email.com" 
              required 
              disabled={isLoading}
            />
            <Button type="submit" className="w-full h-12 rounded-xl font-black uppercase tracking-widest shadow-lg shadow-primary-500/20" isLoading={isLoading}>
              Send Recovery Link
            </Button>
          </form>

          <div className="pt-4 border-t dark:border-slate-800">
            <button 
              onClick={() => setShowTroubleshooter(!showTroubleshooter)}
              className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-primary-500 transition-colors"
            >
              <FiTerminal /> {showTroubleshooter ? 'Hide' : 'Show'} Troubleshooter
            </button>
            
            {showTroubleshooter && (
              <div className="mt-4 p-4 bg-slate-900 rounded-2xl space-y-4 font-mono text-[10px]">
                <div className="space-y-1">
                  <p className="text-primary-400 font-bold">1. VERIFY THIS URL IN SUPABASE:</p>
                  <p className="text-slate-300 break-all bg-black/30 p-2 rounded border border-white/5">{redirectUrl}</p>
                  <p className="text-slate-500 italic">This must be added in: Auth -{'>'} URL Configuration -{'>'} Redirect URLs</p>
                </div>
                <div className="space-y-1">
                  <p className="text-amber-400 font-bold">2. EMAIL PROVIDER STATUS:</p>
                  <p className="text-slate-300">Using: Supabase Default SMTP</p>
                  <p className="text-slate-500 italic">Default providers have low reputation. Link might be in Spam.</p>
                </div>
                <a href="https://supabase.com/dashboard/project/_/auth/url-configuration" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary-500 underline">
                  Open Supabase Config <FiExternalLink size={10} />
                </a>
              </div>
            )}
          </div>

          <p className="text-center text-sm text-slate-500">
            Remembered? <Link to="/login" className="font-bold text-primary-600 hover:underline">Log in</Link>
          </p>
        </div>
      );
    }